import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Avatar, Checkbox,
  List, ListItemButton, ListItemAvatar, ListItemText, CircularProgress,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import { listUsersPublic } from '../../services/userService';

export interface ConferenceCallDialogProps {
  open: boolean;
  onClose: () => void;
  onStart: (userIds: string[]) => void | Promise<void>;
  /** Aktualnie zalogowany użytkownik – wyłączamy go z listy. */
  currentUserId?: string | null;
  /** Identyfikatory użytkowników online (do oznaczania zielonej kropki i sortowania). */
  onlineUserIds?: string[];
  /** Stan „dzwonienia w toku” — blokuje przyciski i pokazuje spinner. */
  starting?: boolean;
}

interface UserRow {
  user_id: string;
  display_name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
  role?: string | null;
}

const ConferenceCallDialog: React.FC<ConferenceCallDialogProps> = ({
  open, onClose, onStart, currentUserId, onlineUserIds, starting,
}) => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setSelected(new Set());
    setFilter('');
    (async () => {
      try {
        const u = await listUsersPublic().catch(() => []);
        if (cancelled) return;
        setUsers((u as UserRow[]) || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const onlineSet = useMemo(() => new Set((onlineUserIds || []).map(String)), [onlineUserIds]);

  const filteredUsers = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = users.filter((u) => u.user_id !== currentUserId);
    list.sort((a, b) => {
      const ao = onlineSet.has(a.user_id) ? 0 : 1;
      const bo = onlineSet.has(b.user_id) ? 0 : 1;
      if (ao !== bo) return ao - bo;
      const an = (a.display_name || a.email || a.user_id || '').toLowerCase();
      const bn = (b.display_name || b.email || b.user_id || '').toLowerCase();
      return an.localeCompare(bn);
    });
    if (!q) return list;
    return list.filter((u) => {
      const name = (u.display_name || '').toLowerCase();
      const mail = (u.email || '').toLowerCase();
      return name.includes(q) || mail.includes(q);
    });
  }, [users, filter, currentUserId, onlineSet]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleStart = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    await onStart(ids);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GroupsIcon /> Telekonferencja
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Wybierz osoby, do których jednocześnie zadzwonisz. Wszyscy dostaną ten sam link Meet.
        </Typography>

        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Szukaj po imieniu lub e‑mailu…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ mb: 1.5 }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
            {filteredUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                Brak użytkowników do wybrania.
              </Typography>
            ) : (
              filteredUsers.map((u) => {
                const isOnline = onlineSet.has(u.user_id);
                const isSelected = selected.has(u.user_id);
                return (
                  <ListItemButton key={u.user_id} onClick={() => toggle(u.user_id)} dense>
                    <Checkbox edge="start" checked={isSelected} tabIndex={-1} disableRipple />
                    <ListItemAvatar>
                      <Box sx={{ position: 'relative' }}>
                        <Avatar src={u.avatar_url || undefined} sx={{ width: 32, height: 32 }}>
                          {(u.display_name || u.email || '?').slice(0, 1).toUpperCase()}
                        </Avatar>
                        {isOnline && (
                          <Box sx={{
                            position: 'absolute', right: -2, bottom: -2,
                            width: 10, height: 10, borderRadius: '50%',
                            bgcolor: '#22c55e', border: '2px solid #fff',
                          }} />
                        )}
                      </Box>
                    </ListItemAvatar>
                    <ListItemText
                      primary={u.display_name || u.email || u.user_id}
                      secondary={u.email && u.email !== u.display_name ? u.email : null}
                    />
                  </ListItemButton>
                );
              })
            )}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {selected.size > 0 ? `Wybrano: ${selected.size}` : 'Nikt nie jest jeszcze wybrany'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={!!starting}>Anuluj</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={selected.size === 0 || !!starting}
            startIcon={starting ? <CircularProgress size={16} color="inherit" /> : <GroupsIcon />}
            onClick={handleStart}
          >
            {starting ? 'Łączenie…' : `Zadzwoń (${selected.size})`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ConferenceCallDialog;
